import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { LicenseSystemSubscriptionViewModel } from 'src/viewModels/LicenseSystem';
import { Observable } from 'zen-observable-ts';
import AppContext from '../AppContext';
import { CompleteReactivateLicenseSystemPayload, ReactivateLicenseSystemInput, ReactivateLicenseSystemPayload } from './generated/types';
import { mapSubscriptionResponseToViewModel } from './mappers/licenseSystem';

const mutation = `mutation ReactivateLicenseSystem($input: ReactivateLicenseSystemInput!) {
    reactivateLicenseSystem(input: $input) {
        succeeded
        messageId
    }
}`;

const onCompleteReactivateLicenseSystem = `subscription OnCompleteReactivateLicenseSystem($messageId: String!) {
    onCompleteReactivateLicenseSystem(messageId: $messageId) {
        messageId
        data
        licenseSystemId
        error {
            errorMessage
            errorType
        }
    }
  }`;

interface RepairLicenseSystemSubscription {
    data: {
        onCompleteReactivateLicenseSystem: CompleteReactivateLicenseSystemPayload;
    };
};

interface RepairLicenseSystemHookInput {
    orgId: string;
    onMutationStart: () => void;
    onSubscriptionResponse: (args: LicenseSystemSubscriptionViewModel) => void;
    selectedLicenseSystemId: string;
}

export const useRepairLicenseSystem = (
    { orgId, onMutationStart, onSubscriptionResponse, selectedLicenseSystemId }: RepairLicenseSystemHookInput,
) => {
    const context = useContext(AppContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (file: string) => {

            onMutationStart();

            const input: ReactivateLicenseSystemInput = { orgId, file, licenseSystemId: selectedLicenseSystemId };

            const { data } = await (context.api.graphql({
                query: mutation,
                variables: { input },
            }) as Promise<{
                data: {
                    reactivateLicenseSystem: ReactivateLicenseSystemPayload;
                };
            }>);

            return data.reactivateLicenseSystem;
        },
        onSuccess: (data, _variables, _ctx) => {

            const qraphqlObserver = context.api.graphql({
                query: onCompleteReactivateLicenseSystem,
                variables: { messageId: data.messageId },
            }) as unknown as Observable<RepairLicenseSystemSubscription>;

            const subscription = qraphqlObserver.subscribe({
                next: (payload) => {
                    const response = payload.data.onCompleteReactivateLicenseSystem;

                    // Handle response
                    onSubscriptionResponse(mapSubscriptionResponseToViewModel(response));

                    // invalidateQueries
                    void queryClient.invalidateQueries({ queryKey: ['getOrganization', orgId] });

                    subscription.unsubscribe();
                },
                error: (error) => {
                    console.error('Subscription error:', error);
                    subscription.unsubscribe();
                },
            });
        },
    });
};